import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Tag, Plus, Pencil, Trash2, Save, Loader2, ArrowLeft, Eye, EyeOff,
  ListChecks, HelpCircle, Sparkles, ExternalLink, Image as ImageIcon, Upload, X, Ticket, Copy,
} from "lucide-react";
import { api, formatCurrency } from "@/lib/api";
import { toast } from "sonner";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { DatePicker } from "@/components/DatePicker";

const HIW_QUILL_MODULES = {
  toolbar: [
    [{ header: [false, 3, 4] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};
const HIW_EMOJI_SET = ["✅", "🚀", "💼", "⚡", "📧", "🎯", "💡", "📊", "🔒", "📱", "🌟", "🏆", "💰", "⏱", "📈", "🤝", "🛡", "✨", "🎉", "👍", "📝", "📞", "🔔", "🏠", "🚗", "👨‍👩‍👧", "🐕", "🏥", "✈️", "❤️"];
const HIW_SYMBOL_SET = ["→", "←", "↑", "↓", "✓", "✗", "★", "☆", "♥", "•", "◦", "§", "©", "®", "™", "€", "$", "£", "¥", "%", "°", "±", "×", "÷", "≈", "≠", "≤", "≥"];

function HiwRichEditor({ value, onChange, testId }) {
  const insertAtCursor = (s) => onChange((value || "") + s);
  return (
    <div className="space-y-1.5" data-testid={testId}>
      <div className="flex flex-wrap gap-1 p-2 bg-slate-50 border border-slate-200 rounded-t-md">
        <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 self-center mr-1">Emoji:</span>
        {HIW_EMOJI_SET.map((e) => (
          <button key={e} type="button" onClick={() => insertAtCursor(e)} className="text-base hover:bg-white rounded px-1 transition-colors" title={`Inserisci ${e}`}>
            {e}
          </button>
        ))}
        <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 self-center mx-1 ml-2">Simboli:</span>
        {HIW_SYMBOL_SET.map((s) => (
          <button key={s} type="button" onClick={() => insertAtCursor(s)} className="text-sm font-mono hover:bg-white rounded px-1.5 transition-colors text-slate-700" title={`Inserisci ${s}`}>
            {s}
          </button>
        ))}
      </div>
      <ReactQuill theme="snow" value={value || ""} onChange={onChange} modules={HIW_QUILL_MODULES} className="bg-white" />
    </div>
  );
}

const PLAN_EMPTY = {
  name: "", price: 0, annual_price: "", annual_discount_percent: "",
  // 🆕 30/07/2026 — Prezzo/sconto semestrale
  semestral_price: "", semestral_discount_percent: "",
  currency: "EUR", billing_cycle: "annuale",
  duration_days: 365, features: [], badge: "", active: true, order: 0,
  max_clients: "", max_policies: "", max_users: "",
};

const COUPON_EMPTY = {
  code: "", discount_percent: "", discount_amount: "",
  valid_from: "", valid_until: "", max_uses: "",
  applies_to_plans: [], applies_to_cycle: "",
  notes: "", active: true,
};

const CYCLES = [
  { v: "mensile", l: "Mensile" },
  { v: "semestrale", l: "Semestrale" },
  { v: "annuale", l: "Annuale" },
  { v: "una_tantum", l: "Una tantum" },
];

export default function LandingManager() {
  const nav = useNavigate();
  const [settings, setSettings] = useState(null);
  const [plans, setPlans] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);

  const [planDialog, setPlanDialog] = useState(null); // {mode: "create"|"edit", form}
  const [featuresText, setFeaturesText] = useState("");
  const [couponDialog, setCouponDialog] = useState(null); // {mode, form}
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, p, c] = await Promise.all([
        api.get("/superadmin/landing-settings"),
        api.get("/superadmin/pricing-plans"),
        api.get("/superadmin/coupons"),
      ]);
      setSettings(s.data);
      setPlans(p.data || []);
      setCoupons(c.data || []);
    } catch { toast.error("Errore caricamento"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const { data } = await api.put("/superadmin/landing-settings", settings);
      setSettings(data);
      toast.success("Impostazioni salvate");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setSavingSettings(false); }
  };

  const toggleEnabled = async (v) => {
    setSettings((s) => ({ ...s, enabled: v }));
    try {
      await api.put("/superadmin/landing-settings", { ...settings, enabled: v });
      toast.success(v ? "Landing pubblicata" : "Landing disattivata");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const openPlanCreate = () => {
    setPlanDialog({ mode: "create", form: { ...PLAN_EMPTY } });
    setFeaturesText("");
  };
  const openPlanEdit = (p) => {
    setPlanDialog({ mode: "edit", form: {
      ...p,
      badge: p.badge || "",
      annual_price: p.annual_price ?? "",
      annual_discount_percent: p.annual_discount_percent ?? "",
      semestral_price: p.semestral_price ?? "",
      semestral_discount_percent: p.semestral_discount_percent ?? "",
      max_clients: p.max_clients ?? "",
      max_policies: p.max_policies ?? "",
      max_users: p.max_users ?? "",
    } });
    setFeaturesText((p.features || []).join("\n"));
  };
  const savePlan = async () => {
    const f = { ...planDialog.form };
    if (!f.name.trim()) { toast.error("Nome obbligatorio"); return; }
    f.features = featuresText.split("\n").map((x) => x.trim()).filter(Boolean);
    f.price = Number(f.price) || 0;
    f.duration_days = Number(f.duration_days) || 365;
    f.order = Number(f.order) || 0;
    // annual_price / annual_discount_percent: optional numbers
    f.annual_price = f.annual_price === "" || f.annual_price === null || f.annual_price === undefined ? null : Number(f.annual_price);
    f.annual_discount_percent = f.annual_discount_percent === "" || f.annual_discount_percent === null || f.annual_discount_percent === undefined ? null : Number(f.annual_discount_percent);
    // 🆕 semestral_price / semestral_discount_percent: optional numbers
    f.semestral_price = f.semestral_price === "" || f.semestral_price === null || f.semestral_price === undefined ? null : Number(f.semestral_price);
    f.semestral_discount_percent = f.semestral_discount_percent === "" || f.semestral_discount_percent === null || f.semestral_discount_percent === undefined ? null : Number(f.semestral_discount_percent);
    // max_clients / max_policies: optional → vuoto/0/null = illimitato
    const _normLimit = (v) => {
      if (v === "" || v === null || v === undefined) return null;
      const n = Number(v);
      if (!Number.isFinite(n) || n <= 0) return null;
      return n;
    };
    f.max_clients = _normLimit(f.max_clients);
    f.max_policies = _normLimit(f.max_policies);
    f.max_users = _normLimit(f.max_users);
    if (!f.badge) f.badge = null;
    setBusy(true);
    try {
      if (planDialog.mode === "create") {
        await api.post("/superadmin/pricing-plans", f);
        toast.success("Piano creato");
      } else {
        await api.put(`/superadmin/pricing-plans/${f.id}`, f);
        toast.success("Piano aggiornato");
      }
      setPlanDialog(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };
  const deletePlan = async (p) => {
    if (!window.confirm(`Eliminare definitivamente il piano "${p.name}"?`)) return;
    try {
      await api.delete(`/superadmin/pricing-plans/${p.id}`);
      toast.success("Piano eliminato"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const duplicatePlan = async (p) => {
    if (!window.confirm(`Duplicare il piano "${p.name}"?\nIl nuovo piano sarà disattivato di default — modificalo e attivalo prima di mostrarlo sulla landing.`)) return;
    try {
      const { data } = await api.post(`/superadmin/pricing-plans/${p.id}/duplicate`);
      toast.success(`Piano duplicato come "${data.name}"`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  // ---------- Coupons ----------
  const openCouponCreate = () => setCouponDialog({ mode: "create", form: { ...COUPON_EMPTY } });
  const openCouponEdit = (c) => setCouponDialog({
    mode: "edit",
    form: {
      ...c,
      discount_percent: c.discount_percent ?? "",
      discount_amount: c.discount_amount ?? "",
      max_uses: c.max_uses ?? "",
      valid_from: c.valid_from ? c.valid_from.slice(0, 10) : "",
      valid_until: c.valid_until ? c.valid_until.slice(0, 10) : "",
      applies_to_plans: c.applies_to_plans || [],
      applies_to_cycle: c.applies_to_cycle || "",
      notes: c.notes || "",
    },
  });
  const saveCoupon = async () => {
    const f = { ...couponDialog.form };
    if (!f.code || !f.code.trim()) { toast.error("Codice coupon obbligatorio"); return; }
    const payload = {
      code: f.code.trim().toUpperCase(),
      discount_percent: f.discount_percent === "" || f.discount_percent === null ? null : Number(f.discount_percent),
      discount_amount: f.discount_amount === "" || f.discount_amount === null ? null : Number(f.discount_amount),
      max_uses: f.max_uses === "" || f.max_uses === null ? null : Number(f.max_uses),
      valid_from: f.valid_from ? new Date(f.valid_from + "T00:00:00Z").toISOString() : null,
      valid_until: f.valid_until ? new Date(f.valid_until + "T23:59:59Z").toISOString() : null,
      applies_to_plans: f.applies_to_plans || [],
      applies_to_cycle: f.applies_to_cycle || null,
      notes: f.notes || "",
      active: !!f.active,
    };
    if (!payload.discount_percent && !payload.discount_amount) {
      toast.error("Specifica almeno una % di sconto o un importo"); return;
    }
    setBusy(true);
    try {
      if (couponDialog.mode === "create") {
        await api.post("/superadmin/coupons", payload);
        toast.success("Coupon creato");
      } else {
        await api.put(`/superadmin/coupons/${couponDialog.form.id}`, payload);
        toast.success("Coupon aggiornato");
      }
      setCouponDialog(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };
  const deleteCoupon = async (c) => {
    if (!window.confirm(`Eliminare il coupon "${c.code}"?`)) return;
    try {
      await api.delete(`/superadmin/coupons/${c.id}`);
      toast.success("Coupon eliminato"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const toggleCouponPlan = (planId) => {
    const cur = couponDialog.form.applies_to_plans || [];
    const next = cur.includes(planId) ? cur.filter((x) => x !== planId) : [...cur, planId];
    setCouponDialog({ ...couponDialog, form: { ...couponDialog.form, applies_to_plans: next } });
  };

  const updateHIW = (idx, field, value) => {
    const arr = [...(settings.how_it_works || [])];
    arr[idx] = { ...arr[idx], [field]: value };
    setSettings({ ...settings, how_it_works: arr });
  };
  const addHIW = () => setSettings({ ...settings, how_it_works: [...(settings.how_it_works || []), { title: "", description: "" }] });
  const removeHIW = (idx) => setSettings({ ...settings, how_it_works: settings.how_it_works.filter((_, i) => i !== idx) });

  const updateFAQ = (idx, field, value) => {
    const arr = [...(settings.faq || [])];
    arr[idx] = { ...arr[idx], [field]: value };
    setSettings({ ...settings, faq: arr });
  };
  const addFAQ = () => setSettings({ ...settings, faq: [...(settings.faq || []), { question: "", answer: "" }] });
  const removeFAQ = (idx) => setSettings({ ...settings, faq: settings.faq.filter((_, i) => i !== idx) });

  const [uploadingHero, setUploadingHero] = useState(false);
  const uploadHeroImage = async (file) => {
    if (!file) return;
    setUploadingHero(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/superadmin/landing-settings/hero-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSettings((s) => ({ ...s, hero_image_url: data.hero_image_url }));
      toast.success("Immagine caricata");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore upload"); }
    finally { setUploadingHero(false); }
  };
  const removeHeroImage = async () => {
    if (!window.confirm("Rimuovere l'immagine hero?")) return;
    try {
      await api.delete("/superadmin/landing-settings/hero-image");
      setSettings((s) => ({ ...s, hero_image_url: null }));
      toast.success("Immagine rimossa");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  // 🆕 Galleria immagini ruotanti nella hero
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const uploadGalleryImage = async (file) => {
    if (!file) return;
    setUploadingGallery(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/superadmin/landing-settings/gallery-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSettings((s) => ({ ...s, gallery: [...(s.gallery || []), data] }));
      toast.success("Immagine aggiunta alla galleria");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore upload"); }
    finally { setUploadingGallery(false); }
  };
  const removeGalleryImage = async (item_id) => {
    if (!window.confirm("Rimuovere questa immagine dalla galleria?")) return;
    try {
      await api.delete(`/superadmin/landing-settings/gallery-image/${item_id}`);
      setSettings((s) => ({ ...s, gallery: (s.gallery || []).filter((g) => g.id !== item_id) }));
      toast.success("Immagine rimossa");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  if (loading || !settings) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Caricamento…</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-gradient-to-r from-violet-700 to-indigo-700 text-white shadow-md">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button data-testid="back-to-sa" onClick={() => nav("/superadmin")} className="hover:bg-white/10 rounded p-1">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <Sparkles className="h-5 w-5" />
            <div className="leading-tight">
              <div className="font-display font-semibold">Gestione Landing & Piani</div>
              <div className="text-[11px] text-violet-200/90">Super-Admin</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs opacity-80">
              {settings.enabled ? "🟢 Landing attiva" : "⚫ Landing disattiva"}
            </span>
            {settings.enabled && (
              <a
                href="/landing"
                target="_blank"
                rel="noreferrer"
                data-testid="preview-landing"
                className="text-xs underline opacity-90 hover:opacity-100"
              >
                <ExternalLink className="h-3 w-3 inline mr-0.5" /> Apri landing
              </a>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8" data-testid="landing-manager">
        {/* Banner toggle on/off */}
        <Card className={`mb-6 ${settings.enabled ? "border-emerald-300 bg-emerald-50/60" : "border-slate-300 bg-slate-50"}`}>
          <CardContent className="py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {settings.enabled ? <Eye className="h-6 w-6 text-emerald-600" /> : <EyeOff className="h-6 w-6 text-slate-400" />}
              <div>
                <div className="font-semibold">{settings.enabled ? "Landing pubblica attiva" : "Landing non pubblica"}</div>
                <p className="text-xs text-slate-600">
                  {settings.enabled
                    ? <>Condividi questo link con le agenzie interessate:
                        <code className="bg-white px-1.5 py-0.5 rounded ml-1 text-blue-700 font-mono text-[11px]" data-testid="landing-public-url">
                          {window.location.origin}/landing
                        </code>
                        <button
                          type="button"
                          data-testid="copy-landing-url"
                          onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/landing`); toast.success("Link copiato"); }}
                          className="ml-2 text-blue-600 hover:underline text-xs"
                        >
                          📋 copia
                        </button>
                      </>
                    : <>Quando attivata, sarà visibile su <code className="bg-white px-1 rounded">/landing</code>. Comparirà anche come link &quot;Vedi i piani&quot; nelle pagine di login e registrazione.</>}
                </p>
              </div>
            </div>
            <Switch
              data-testid="toggle-landing-enabled"
              checked={!!settings.enabled}
              onCheckedChange={toggleEnabled}
            />
          </CardContent>
        </Card>

        <Tabs defaultValue="plans" className="space-y-4">
          <TabsList>
            <TabsTrigger value="plans" data-testid="tab-plans"><Tag className="h-4 w-4 mr-1" /> Piani tariffari ({plans.length})</TabsTrigger>
            <TabsTrigger value="coupons" data-testid="tab-coupons"><Ticket className="h-4 w-4 mr-1" /> Coupon ({coupons.length})</TabsTrigger>
            <TabsTrigger value="hero" data-testid="tab-hero"><Sparkles className="h-4 w-4 mr-1" /> Hero & Footer</TabsTrigger>
            <TabsTrigger value="hiw" data-testid="tab-hiw"><ListChecks className="h-4 w-4 mr-1" /> Come funziona ({(settings.how_it_works || []).length})</TabsTrigger>
            <TabsTrigger value="faq" data-testid="tab-faq"><HelpCircle className="h-4 w-4 mr-1" /> FAQ ({(settings.faq || []).length})</TabsTrigger>
          </TabsList>

          <TabsContent value="plans">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">Piani disponibili</CardTitle>
                <Button data-testid="add-plan-btn" onClick={openPlanCreate} className="bg-violet-600 hover:bg-violet-700">
                  <Plus className="h-4 w-4 mr-1" /> Nuovo piano
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                {plans.length === 0 ? (
                  <div className="text-center py-10 text-slate-400 text-sm">Nessun piano creato. Clicca &quot;Nuovo piano&quot;.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
                        <tr>
                          <th className="text-left px-4 py-2.5">Nome</th>
                          <th className="text-left px-3 py-2.5">Prezzo</th>
                          <th className="text-left px-3 py-2.5">Frazionamento</th>
                          <th className="text-center px-3 py-2.5">Durata (gg)</th>
                          <th className="text-center px-3 py-2.5">Funzioni</th>
                          <th className="text-center px-3 py-2.5">Ordine</th>
                          <th className="text-center px-3 py-2.5">Stato</th>
                          <th className="text-right px-4 py-2.5">Azioni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {plans.map((p) => (
                          <tr key={p.id} className="border-t border-slate-100" data-testid={`plan-row-${p.id}`}>
                            <td className="px-4 py-3 font-medium">
                              {p.name}
                              {p.badge && <span className="ml-2 inline-block px-2 py-0.5 text-[10px] rounded bg-amber-100 text-amber-800 border border-amber-300">{p.badge}</span>}
                            </td>
                            <td className="px-3 py-3">
                              {formatCurrency(p.price)}
                              {p.annual_price && <div className="text-[10px] text-blue-600">+ {formatCurrency(p.annual_price)}/anno</div>}
                            </td>
                            <td className="px-3 py-3 text-xs text-slate-600">{p.billing_cycle}</td>
                            <td className="px-3 py-3 text-center">{p.duration_days}</td>
                            <td className="px-3 py-3 text-center text-xs text-slate-500">{(p.features || []).length}</td>
                            <td className="px-3 py-3 text-center">{p.order}</td>
                            <td className="px-3 py-3 text-center">
                              <span className={`text-[10px] px-2 py-0.5 rounded ${p.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
                                {p.active ? "Attivo" : "Disattivato"}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="inline-flex gap-1.5">
                                <Button size="sm" variant="outline" data-testid={`edit-plan-${p.id}`} onClick={() => openPlanEdit(p)} className="h-7 text-xs">
                                  <Pencil className="h-3 w-3 mr-1" /> Modifica
                                </Button>
                                <Button size="sm" variant="outline" data-testid={`dup-plan-${p.id}`} onClick={() => duplicatePlan(p)} className="h-7 text-xs text-blue-600 hover:bg-blue-50" title="Duplica piano">
                                  <Copy className="h-3 w-3 mr-1" /> Duplica
                                </Button>
                                <Button size="sm" variant="outline" data-testid={`del-plan-${p.id}`} onClick={() => deletePlan(p)} className="h-7 text-xs text-rose-600 hover:bg-rose-50">
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="coupons">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">Codici sconto / Coupon</CardTitle>
                <Button data-testid="add-coupon-btn" onClick={openCouponCreate} className="bg-violet-600 hover:bg-violet-700">
                  <Plus className="h-4 w-4 mr-1" /> Nuovo coupon
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                {coupons.length === 0 ? (
                  <div className="text-center py-10 text-slate-400 text-sm">
                    Nessun coupon creato. Clicca &quot;Nuovo coupon&quot; per generare codici sconto da condividere con le nuove agenzie.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
                        <tr>
                          <th className="text-left px-4 py-2.5">Codice</th>
                          <th className="text-left px-3 py-2.5">Sconto</th>
                          <th className="text-left px-3 py-2.5">Validità</th>
                          <th className="text-center px-3 py-2.5">Usi</th>
                          <th className="text-center px-3 py-2.5">Frazionamento</th>
                          <th className="text-center px-3 py-2.5">Stato</th>
                          <th className="text-right px-4 py-2.5">Azioni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {coupons.map((c) => (
                          <tr key={c.id} className="border-t border-slate-100" data-testid={`coupon-row-${c.id}`}>
                            <td className="px-4 py-3">
                              <code className="font-mono font-semibold text-violet-700 bg-violet-50 px-2 py-0.5 rounded">{c.code}</code>
                              {c.notes && <div className="text-[10px] text-slate-500 mt-1">{c.notes}</div>}
                            </td>
                            <td className="px-3 py-3">
                              {c.discount_percent ? `${c.discount_percent}%` : c.discount_amount ? formatCurrency(c.discount_amount) : "—"}
                            </td>
                            <td className="px-3 py-3 text-xs text-slate-600">
                              {c.valid_from && <div>Dal: {new Date(c.valid_from).toLocaleDateString("it-IT")}</div>}
                              {c.valid_until && <div>Al: {new Date(c.valid_until).toLocaleDateString("it-IT")}</div>}
                              {!c.valid_from && !c.valid_until && <span className="text-slate-400">Sempre</span>}
                            </td>
                            <td className="px-3 py-3 text-center text-xs">
                              <span className="font-mono">{c.used_count || 0}</span>
                              {c.max_uses ? <span className="text-slate-400"> / {c.max_uses}</span> : <span className="text-slate-400"> / ∞</span>}
                            </td>
                            <td className="px-3 py-3 text-center text-xs text-slate-600 capitalize">{c.applies_to_cycle || "qualsiasi"}</td>
                            <td className="px-3 py-3 text-center">
                              <span className={`text-[10px] px-2 py-0.5 rounded ${c.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
                                {c.active ? "Attivo" : "Disattivato"}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="inline-flex gap-1.5">
                                <Button size="sm" variant="outline" data-testid={`edit-coupon-${c.id}`} onClick={() => openCouponEdit(c)} className="h-7 text-xs">
                                  <Pencil className="h-3 w-3 mr-1" /> Modifica
                                </Button>
                                <Button size="sm" variant="outline" data-testid={`del-coupon-${c.id}`} onClick={() => deleteCoupon(c)} className="h-7 text-xs text-rose-600 hover:bg-rose-50">
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="hero">
            <Card>
              <CardHeader><CardTitle className="text-base">Sezione hero & footer</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>Titolo principale (Hero)</Label>
                  <Input data-testid="hero-title" value={settings.hero_title} onChange={(e) => setSettings({ ...settings, hero_title: e.target.value })} />
                </div>
                <div>
                  <Label>Sottotitolo</Label>
                  <Textarea data-testid="hero-subtitle" rows={2} value={settings.hero_subtitle} onChange={(e) => setSettings({ ...settings, hero_subtitle: e.target.value })} />
                </div>
                <div>
                  <Label>Testo pulsante CTA</Label>
                  <Input data-testid="hero-cta" value={settings.hero_cta} onChange={(e) => setSettings({ ...settings, hero_cta: e.target.value })} />
                </div>
                <div className="border-t border-slate-200 pt-3">
                  <Label className="flex items-center gap-1.5"><ImageIcon className="h-4 w-4 text-blue-600" /> Immagine hero (screenshot app / illustrazione)</Label>
                  {settings.hero_image_url ? (
                    <div className="mt-2 space-y-2">
                      <img src={settings.hero_image_url} alt="Hero preview" className="rounded-lg border border-slate-200 max-h-48 object-contain bg-slate-50" data-testid="hero-image-preview" />
                      <div className="flex gap-2">
                        <label className="inline-flex items-center cursor-pointer">
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            className="hidden"
                            data-testid="hero-image-replace"
                            onChange={(e) => uploadHeroImage(e.target.files?.[0])}
                          />
                          <span className="inline-flex items-center text-xs border border-slate-300 rounded px-3 py-1.5 hover:bg-slate-50">
                            {uploadingHero ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Upload className="h-3 w-3 mr-1" />}
                            Sostituisci
                          </span>
                        </label>
                        <Button variant="outline" size="sm" onClick={removeHeroImage} data-testid="hero-image-remove" className="text-rose-600 hover:bg-rose-50 h-8 text-xs">
                          <X className="h-3 w-3 mr-1" /> Rimuovi
                        </Button>
                      </div>
                      <p className="text-[10px] text-slate-400">Suggerimento: usa uno screenshot 16:9 della dashboard, PNG o WEBP, max 4 MB.</p>
                    </div>
                  ) : (
                    <div className="mt-2">
                      <label className="block cursor-pointer">
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp"
                          className="hidden"
                          data-testid="hero-image-upload"
                          onChange={(e) => uploadHeroImage(e.target.files?.[0])}
                        />
                        <div className="border-2 border-dashed border-slate-300 rounded-lg py-6 px-4 text-center hover:border-blue-400 hover:bg-blue-50/30 transition">
                          {uploadingHero ? (
                            <><Loader2 className="h-6 w-6 mx-auto text-blue-600 animate-spin" /><p className="text-xs mt-1 text-slate-500">Caricamento…</p></>
                          ) : (
                            <><Upload className="h-6 w-6 mx-auto text-slate-400" /><p className="text-xs mt-1 text-slate-500">Clicca per caricare (PNG/JPG/WEBP · max 4 MB)</p></>
                          )}
                        </div>
                      </label>
                    </div>
                  )}
                </div>
                <div>
                  <Label>Testo footer (es. P.IVA, contatti, copyright)</Label>
                  <Textarea data-testid="footer-text" rows={3} value={settings.footer_text || ""} onChange={(e) => setSettings({ ...settings, footer_text: e.target.value })} placeholder="© 2026 Tua Azienda — info@example.it" />
                </div>

                {/* 🆕 Messaggio di conferma "Richiedi una demo" */}
                <div className="border-t border-slate-200 pt-4">
                  <Label className="flex items-center gap-1.5 mb-1">
                    ✨ Messaggio di conferma dopo l&apos;invio richiesta demo
                  </Label>
                  <p className="text-[11px] text-slate-500 mb-2">
                    Testo mostrato in un riquadro fisso all&apos;interno del popup &quot;Richiedi una demo&quot; dopo che l&apos;utente ha inviato il form. L&apos;utente lo vedrà finché non clicca &quot;Chiudi&quot;.
                  </p>
                  <Textarea
                    data-testid="demo-request-confirmation-message"
                    rows={4}
                    value={settings.demo_request_confirmation_message || ""}
                    onChange={(e) => setSettings({ ...settings, demo_request_confirmation_message: e.target.value })}
                    placeholder="Grazie! La tua richiesta è stata inviata. Un nostro consulente ti contatterà a breve."
                  />
                </div>

                {/* 🆕 Galleria immagini ruotanti */}
                <div className="border-t border-slate-200 pt-4">
                  <Label className="flex items-center gap-1.5 mb-2">
                    <ImageIcon className="h-4 w-4 text-emerald-600" /> Galleria immagini ruotanti (opzionale)
                  </Label>
                  <p className="text-[11px] text-slate-500 mb-3">
                    Aggiungi <strong>più immagini</strong> (screenshot, mockup, foto della sede…) che si alterneranno automaticamente nella sezione hero della landing insieme all&apos;immagine principale.
                    Ogni immagine ruota ogni ~5 secondi. PNG/JPG/WEBP · max 4 MB ciascuna.
                  </p>
                  {(settings.gallery && settings.gallery.length > 0) && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mb-3" data-testid="gallery-grid">
                      {settings.gallery.map((g) => (
                        <div key={g.id} className="relative group rounded-lg border border-slate-200 overflow-hidden bg-slate-50" data-testid={`gallery-item-${g.id}`}>
                          <img src={g.url} alt="Gallery" className="w-full h-24 object-cover" />
                          <button
                            type="button"
                            onClick={() => removeGalleryImage(g.id)}
                            className="absolute top-1 right-1 p-1 rounded-full bg-white/90 text-rose-600 hover:bg-rose-100 shadow opacity-90 hover:opacity-100 transition-opacity"
                            title="Rimuovi immagine"
                            data-testid={`gallery-remove-${g.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <label className="cursor-pointer inline-block">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                      data-testid="gallery-upload"
                      onChange={(e) => uploadGalleryImage(e.target.files?.[0])}
                    />
                    <div className="border-2 border-dashed border-emerald-300 rounded-lg px-4 py-3 text-center hover:bg-emerald-50 transition-colors inline-flex items-center gap-2">
                      {uploadingGallery ? (
                        <><Loader2 className="h-4 w-4 animate-spin text-emerald-600" /><span className="text-xs text-emerald-700 font-medium">Caricamento…</span></>
                      ) : (
                        <><Plus className="h-4 w-4 text-emerald-600" /><span className="text-xs text-emerald-700 font-medium">Aggiungi immagine alla galleria</span></>
                      )}
                    </div>
                  </label>
                  {(!settings.gallery || settings.gallery.length === 0) && (
                    <p className="text-[11px] text-slate-400 mt-2 italic">
                      Nessuna immagine in galleria. Carica almeno 2 immagini per attivare la rotazione automatica.
                    </p>
                  )}
                </div>

                <Button data-testid="save-hero" onClick={saveSettings} disabled={savingSettings} className="bg-blue-600 hover:bg-blue-700">
                  {savingSettings ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Salva
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="hiw">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">Sezione &quot;Come funziona&quot;</CardTitle>
                <Button data-testid="add-hiw" onClick={addHIW} size="sm" className="bg-violet-600 hover:bg-violet-700"><Plus className="h-4 w-4 mr-1" /> Aggiungi step</Button>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2">
                  <Label className="text-xs font-semibold">Titolo della sezione</Label>
                  <Input
                    data-testid="hiw-section-title"
                    value={settings.how_it_works_title || ""}
                    onChange={(e) => setSettings({ ...settings, how_it_works_title: e.target.value })}
                    placeholder="Es: Come funziona"
                    maxLength={120}
                  />
                  <Label className="text-xs font-semibold">Sottotitolo / introduzione (opzionale)</Label>
                  <Textarea
                    data-testid="hiw-section-subtitle"
                    rows={2}
                    value={settings.how_it_works_subtitle || ""}
                    onChange={(e) => setSettings({ ...settings, how_it_works_subtitle: e.target.value })}
                    placeholder="Es: Spiega in 4 step semplici come la piattaforma supporta le agenzie."
                    maxLength={400}
                  />
                </div>
                {(settings.how_it_works || []).length === 0 && <div className="text-center py-6 text-slate-400 text-sm">Nessuno step. Clicca &quot;Aggiungi step&quot; per iniziare.</div>}
                {(settings.how_it_works || []).map((s, i) => (
                  <div key={i} className="border border-slate-200 rounded-lg p-3 space-y-2 relative">
                    <span className="absolute -top-2.5 -left-2.5 w-7 h-7 rounded-full bg-violet-600 text-white text-xs flex items-center justify-center font-bold">{i + 1}</span>
                    <div className="flex gap-2">
                      <Input
                        data-testid={`hiw-icon-${i}`}
                        value={s.icon || ""}
                        onChange={(e) => updateHIW(i, "icon", e.target.value)}
                        placeholder="🚀"
                        maxLength={4}
                        className="w-20 text-center text-xl"
                        title="Inserisci una emoji (es. 🚀 💼 ⚡ 📧 ✅) o lascia vuoto per usare il numero"
                      />
                      <Input data-testid={`hiw-title-${i}`} value={s.title || ""} onChange={(e) => updateHIW(i, "title", e.target.value)} placeholder="Titolo step" className="flex-1" />
                    </div>
                    <Textarea data-testid={`hiw-desc-${i}`} rows={2} value={(s.description || "").replace(/<[^>]+>/g, "")} readOnly className="hidden" />
                    <div>
                      <label className="text-[11px] font-medium block mb-1 text-slate-600">Descrizione (rich text + emoji/simboli)</label>
                      <HiwRichEditor
                        testId={`hiw-rich-${i}`}
                        value={s.description || ""}
                        onChange={(v) => updateHIW(i, "description", v)}
                      />
                    </div>
                    <Button data-testid={`hiw-del-${i}`} variant="outline" size="sm" onClick={() => removeHIW(i)} className="text-rose-600 hover:bg-rose-50 h-7 text-xs"><Trash2 className="h-3 w-3 mr-1" /> Rimuovi step</Button>
                  </div>
                ))}
                <Button data-testid="save-hiw" onClick={saveSettings} disabled={savingSettings} className="bg-blue-600 hover:bg-blue-700">
                  {savingSettings ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Salva &quot;Come funziona&quot;
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="faq">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">Sezione FAQ</CardTitle>
                <Button data-testid="add-faq" onClick={addFAQ} size="sm" className="bg-violet-600 hover:bg-violet-700"><Plus className="h-4 w-4 mr-1" /> Aggiungi domanda</Button>
              </CardHeader>
              <CardContent className="space-y-3">
                {(settings.faq || []).length === 0 && <div className="text-center py-6 text-slate-400 text-sm">Nessuna FAQ. Clicca &quot;Aggiungi domanda&quot;.</div>}
                {(settings.faq || []).map((q, i) => (
                  <div key={i} className="border border-slate-200 rounded-lg p-3 space-y-2 relative">
                    <span className="absolute -top-2.5 -left-2.5 w-7 h-7 rounded-full bg-violet-600 text-white text-xs flex items-center justify-center font-bold">?{i + 1}</span>
                    <Input data-testid={`faq-q-${i}`} value={q.question || ""} onChange={(e) => updateFAQ(i, "question", e.target.value)} placeholder="Domanda" />
                    <Textarea data-testid={`faq-a-${i}`} rows={3} value={q.answer || ""} onChange={(e) => updateFAQ(i, "answer", e.target.value)} placeholder="Risposta" />
                    <Button data-testid={`faq-del-${i}`} variant="outline" size="sm" onClick={() => removeFAQ(i)} className="text-rose-600 hover:bg-rose-50 h-7 text-xs"><Trash2 className="h-3 w-3 mr-1" /> Rimuovi</Button>
                  </div>
                ))}
                <Button data-testid="save-faq" onClick={saveSettings} disabled={savingSettings} className="bg-blue-600 hover:bg-blue-700">
                  {savingSettings ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Salva FAQ
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Plan dialog (create/edit) */}
      <Dialog open={!!planDialog} onOpenChange={(o) => !o && setPlanDialog(null)}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="plan-dialog">
          <DialogHeader><DialogTitle>{planDialog?.mode === "create" ? "Nuovo piano" : `Modifica ${planDialog?.form?.name}`}</DialogTitle></DialogHeader>
          {planDialog && (
            <div className="space-y-2.5">
              <div>
                <Label>Nome <span className="text-red-500">*</span></Label>
                <Input data-testid="plan-name" value={planDialog.form.name} onChange={(e) => setPlanDialog({ ...planDialog, form: { ...planDialog.form, name: e.target.value } })} placeholder="Es. Pro" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label>Prezzo (€) <span className="text-[10px] text-slate-400">{planDialog.form.billing_cycle === "mensile" ? "(mensile)" : ""}</span></Label>
                  <Input data-testid="plan-price" type="number" step="0.01" value={planDialog.form.price} onChange={(e) => setPlanDialog({ ...planDialog, form: { ...planDialog.form, price: e.target.value } })} />
                </div>
                <div>
                  <Label>Frazionamento</Label>
                  <select
                    data-testid="plan-cycle"
                    className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm bg-white"
                    value={planDialog.form.billing_cycle}
                    onChange={(e) => setPlanDialog({ ...planDialog, form: { ...planDialog.form, billing_cycle: e.target.value } })}
                  >
                    {CYCLES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 rounded-md border border-blue-200 bg-blue-50/40 p-2">
                <div>
                  <Label className="text-xs">Prezzo annuale (€) <span className="text-[10px] text-slate-400">opzionale</span></Label>
                  <Input
                    data-testid="plan-annual-price"
                    type="number"
                    step="0.01"
                    value={planDialog.form.annual_price ?? ""}
                    onChange={(e) => setPlanDialog({ ...planDialog, form: { ...planDialog.form, annual_price: e.target.value } })}
                    placeholder="Es. 290"
                  />
                  <p className="text-[10px] text-slate-500 mt-0.5">Se valorizzato → abilita toggle mensile/annuale sulla landing.</p>
                </div>
                <div>
                  <Label className="text-xs">Sconto annuale (%) <span className="text-[10px] text-slate-400">badge</span></Label>
                  <Input
                    data-testid="plan-annual-discount"
                    type="number"
                    step="1"
                    value={planDialog.form.annual_discount_percent ?? ""}
                    onChange={(e) => setPlanDialog({ ...planDialog, form: { ...planDialog.form, annual_discount_percent: e.target.value } })}
                    placeholder="Es. 20"
                  />
                  <p className="text-[10px] text-slate-500 mt-0.5">Mostrato come badge &quot;Risparmia X%&quot; sulla landing.</p>
                </div>
              </div>
              {/* 🆕 30/07/2026 — Prezzo/sconto semestrale */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 rounded-md border border-cyan-200 bg-cyan-50/40 p-2">
                <div>
                  <Label className="text-xs">Prezzo semestrale (€) <span className="text-[10px] text-slate-400">opzionale</span></Label>
                  <Input
                    data-testid="plan-semestral-price"
                    type="number"
                    step="0.01"
                    value={planDialog.form.semestral_price ?? ""}
                    onChange={(e) => setPlanDialog({ ...planDialog, form: { ...planDialog.form, semestral_price: e.target.value } })}
                    placeholder="Es. 155"
                  />
                  <p className="text-[10px] text-slate-500 mt-0.5">Se valorizzato → abilita opzione semestrale sulla landing.</p>
                </div>
                <div>
                  <Label className="text-xs">Sconto semestrale (%) <span className="text-[10px] text-slate-400">badge</span></Label>
                  <Input
                    data-testid="plan-semestral-discount"
                    type="number"
                    step="1"
                    value={planDialog.form.semestral_discount_percent ?? ""}
                    onChange={(e) => setPlanDialog({ ...planDialog, form: { ...planDialog.form, semestral_discount_percent: e.target.value } })}
                    placeholder="Es. 10"
                  />
                  <p className="text-[10px] text-slate-500 mt-0.5">Mostrato come badge &quot;Risparmia X%&quot; sulla landing.</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label>Durata abbonamento (giorni)</Label>
                  <Input data-testid="plan-duration" type="number" value={planDialog.form.duration_days} onChange={(e) => setPlanDialog({ ...planDialog, form: { ...planDialog.form, duration_days: e.target.value } })} />
                </div>
                <div>
                  <Label>Ordine</Label>
                  <Input data-testid="plan-order" type="number" value={planDialog.form.order} onChange={(e) => setPlanDialog({ ...planDialog, form: { ...planDialog.form, order: e.target.value } })} />
                </div>
              </div>
              <div>
                <Label>Badge (opzionale)</Label>
                <Input data-testid="plan-badge" value={planDialog.form.badge || ""} onChange={(e) => setPlanDialog({ ...planDialog, form: { ...planDialog.form, badge: e.target.value } })} placeholder="Es. 🔥 Più scelto" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 rounded-md border border-indigo-200 bg-indigo-50/40 p-2">
                <div>
                  <Label className="text-xs">Max clienti <span className="text-[10px] text-slate-400">vuoto = ∞</span></Label>
                  <Input
                    data-testid="plan-max-clients"
                    type="number"
                    min="0"
                    value={planDialog.form.max_clients ?? ""}
                    onChange={(e) => setPlanDialog({ ...planDialog, form: { ...planDialog.form, max_clients: e.target.value } })}
                    placeholder="Es. 500"
                  />
                </div>
                <div>
                  <Label className="text-xs">Max polizze <span className="text-[10px] text-slate-400">vuoto = ∞</span></Label>
                  <Input
                    data-testid="plan-max-policies"
                    type="number"
                    min="0"
                    value={planDialog.form.max_policies ?? ""}
                    onChange={(e) => setPlanDialog({ ...planDialog, form: { ...planDialog.form, max_policies: e.target.value } })}
                    placeholder="Es. 2000"
                  />
                </div>
                <div>
                  <Label className="text-xs">Max utenti <span className="text-[10px] text-slate-400">vuoto = ∞</span></Label>
                  <Input
                    data-testid="plan-max-users"
                    type="number"
                    min="0"
                    value={planDialog.form.max_users ?? ""}
                    onChange={(e) => setPlanDialog({ ...planDialog, form: { ...planDialog.form, max_users: e.target.value } })}
                    placeholder="Es. 5"
                  />
                </div>
                <p className="col-span-2 text-[10px] text-slate-500 mt-0.5">
                  Limiti inclusi nel piano. Le agenzie con questo piano avranno questi limiti di default
                  (sovrascrivibili dal Super-Admin sulla singola agenzia).
                </p>
              </div>
              <div>
                <Label>Funzionalità incluse (una per riga)</Label>
                <Textarea
                  data-testid="plan-features"
                  rows={5}
                  value={featuresText}
                  onChange={(e) => setFeaturesText(e.target.value)}
                  placeholder="Clienti illimitati&#10;Polizze illimitate&#10;Supporto prioritario"
                />
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" data-testid="plan-active" checked={!!planDialog.form.active} onChange={(e) => setPlanDialog({ ...planDialog, form: { ...planDialog.form, active: e.target.checked } })} className="h-4 w-4" />
                <span>Piano attivo (visibile sulla landing)</span>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanDialog(null)}>Annulla</Button>
            <Button data-testid="plan-save" onClick={savePlan} disabled={busy} className="bg-violet-600 hover:bg-violet-700 text-white">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Coupon dialog (create/edit) */}
      <Dialog open={!!couponDialog} onOpenChange={(o) => !o && setCouponDialog(null)}>
        <DialogContent className="max-w-md" data-testid="coupon-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ticket className="h-5 w-5 text-violet-600" />
              {couponDialog?.mode === "create" ? "Nuovo coupon" : `Modifica ${couponDialog?.form?.code}`}
            </DialogTitle>
          </DialogHeader>
          {couponDialog && (
            <div className="space-y-2.5">
              <div>
                <Label>Codice <span className="text-red-500">*</span></Label>
                <Input
                  data-testid="coupon-code"
                  value={couponDialog.form.code}
                  onChange={(e) => setCouponDialog({ ...couponDialog, form: { ...couponDialog.form, code: e.target.value.toUpperCase().replace(/\s+/g, "") } })}
                  placeholder="ES. SCONTO20"
                  className="font-mono uppercase"
                />
                <p className="text-[10px] text-slate-400 mt-0.5">Min 3 caratteri. Verrà convertito in maiuscolo.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label>% di sconto</Label>
                  <Input
                    data-testid="coupon-discount-percent"
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={couponDialog.form.discount_percent}
                    onChange={(e) => setCouponDialog({ ...couponDialog, form: { ...couponDialog.form, discount_percent: e.target.value, discount_amount: "" } })}
                    placeholder="Es. 20"
                  />
                </div>
                <div>
                  <Label>oppure € fisso</Label>
                  <Input
                    data-testid="coupon-discount-amount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={couponDialog.form.discount_amount}
                    onChange={(e) => setCouponDialog({ ...couponDialog, form: { ...couponDialog.form, discount_amount: e.target.value, discount_percent: "" } })}
                    placeholder="Es. 50.00"
                  />
                </div>
              </div>
              <p className="text-[10px] text-slate-400 -mt-1">Specifica solo uno dei due (%/€ fisso).</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label>Valido dal</Label>
                  <DatePicker data-testid="coupon-valid-from" value={couponDialog.form.valid_from}
                    onChange={(e) => setCouponDialog({ ...couponDialog, form: { ...couponDialog.form, valid_from: e.target.value } })} />
                </div>
                <div>
                  <Label>Valido al</Label>
                  <DatePicker data-testid="coupon-valid-until" value={couponDialog.form.valid_until}
                    onChange={(e) => setCouponDialog({ ...couponDialog, form: { ...couponDialog.form, valid_until: e.target.value } })} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label>Max utilizzi</Label>
                  <Input
                    data-testid="coupon-max-uses"
                    type="number"
                    min="1"
                    value={couponDialog.form.max_uses}
                    onChange={(e) => setCouponDialog({ ...couponDialog, form: { ...couponDialog.form, max_uses: e.target.value } })}
                    placeholder="∞ se vuoto"
                  />
                </div>
                <div>
                  <Label>Valido solo per</Label>
                  <select
                    data-testid="coupon-cycle"
                    className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm bg-white"
                    value={couponDialog.form.applies_to_cycle}
                    onChange={(e) => setCouponDialog({ ...couponDialog, form: { ...couponDialog.form, applies_to_cycle: e.target.value } })}
                  >
                    <option value="">Qualsiasi</option>
                    <option value="mensile">Solo mensile</option>
                    <option value="annuale">Solo annuale</option>
                    <option value="una_tantum">Solo una tantum</option>
                  </select>
                </div>
              </div>
              <div>
                <Label>Limita ai piani <span className="text-[10px] text-slate-400">(vuoto = tutti)</span></Label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {plans.length === 0 && <p className="text-xs text-slate-400">Nessun piano. Crea prima i piani.</p>}
                  {plans.map((p) => {
                    const selected = (couponDialog.form.applies_to_plans || []).includes(p.id);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        data-testid={`coupon-plan-${p.id}`}
                        onClick={() => toggleCouponPlan(p.id)}
                        className={`text-xs px-2 py-1 rounded border ${selected ? "bg-violet-600 text-white border-violet-600" : "bg-white border-slate-300 text-slate-700 hover:border-violet-400"}`}
                      >
                        {p.name}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label>Note interne</Label>
                <Input
                  data-testid="coupon-notes"
                  value={couponDialog.form.notes}
                  onChange={(e) => setCouponDialog({ ...couponDialog, form: { ...couponDialog.form, notes: e.target.value } })}
                  placeholder="Es. Promo lancio 2026"
                />
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="coupon-active"
                  checked={!!couponDialog.form.active}
                  onChange={(e) => setCouponDialog({ ...couponDialog, form: { ...couponDialog.form, active: e.target.checked } })}
                  className="h-4 w-4"
                />
                <span>Coupon attivo (utilizzabile dalle agenzie)</span>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCouponDialog(null)}>Annulla</Button>
            <Button data-testid="coupon-save" onClick={saveCoupon} disabled={busy} className="bg-violet-600 hover:bg-violet-700 text-white">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
